import React, { useState, useEffect } from 'react';
import { useSidebar } from '../../contexts/SidebarContext';
import Sidebar from '../../components/ui/Sidebar';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { useUserData } from '../../hooks/useUserData';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useDemo } from '../../contexts/DemoContext';
import { useTour } from '../../contexts/TourContext';
import DemoModeBanner from '../../components/demo/DemoModeBanner';
import VersionSelectionModal from '../../components/simplified/VersionSelectionModal';

const Setup = () => {
  const { sidebarExpanded } = useSidebar();
  const { canWrite } = useSubscription();
  const { isDemoModeActive } = useDemo();
  const effectiveCanWrite = canWrite && !isDemoModeActive;
  const { startSetupTour, autoStartTour } = useTour();
  const [formData, setFormData] = useState({
    currentAge: '',
    retirementYear: '',
    startYear: '',
    projectionHorizon: '20',
    defaultExpenseInflation: '6',
    defaultIncomeGrowth: '3',
    postRetirementPvReturn: '7',
    marginOfSafety: '4'
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [showVersionModal, setShowVersionModal] = useState(false);

  const { userData, loading, error, updateData } = useUserData();

  // Auto-start tour for first-time visitors
  useEffect(() => {
    if (!loading && userData) {
      autoStartTour('setup', startSetupTour);
    }
  }, [loading, userData, autoStartTour, startSetupTour]);

  // Load existing setup data when component mounts
  useEffect(() => {
    if (userData?.setup) {
      setFormData({
        currentAge: userData.setup.currentAge?.toString() || '',
        retirementYear: userData.setup.retirementYear?.toString() || '',
        startYear: userData.setup.startYear?.toString() || '',
        projectionHorizon: userData.setup.projectionHorizon?.toString() || '20',
        defaultExpenseInflation: userData.setup.defaultExpenseInflation?.toString() || '6',
        defaultIncomeGrowth: userData.setup.defaultIncomeGrowth?.toString() || '3',
        postRetirementPvReturn: userData.setup.postRetirementPvReturn?.toString() || '7',
        marginOfSafety: userData.setup.marginOfSafety?.toString() || '4'
      });
      setIsSaved(true);
    }
  }, [userData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Allow numbers or decimals based on field
    const decimalFields = new Set([
      'defaultExpenseInflation',
      'defaultIncomeGrowth',
      'postRetirementPvReturn',
      'marginOfSafety'
    ]);
    if (value) {
      const numberRegex = /^\d+$/;
      const decimalRegex = /^\d*(?:\.|\d+)?\d*$/; // allows '', '1', '1.', '1.2'
      const isValid = decimalFields.has(name) ? decimalRegex.test(value) : numberRegex.test(value);
      if (!isValid) return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Clear saved status when user makes changes
    if (isSaved) {
      setIsSaved(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate current age
    if (!formData.currentAge) {
      newErrors.currentAge = 'Current age is required';
    } else {
      const age = parseInt(formData.currentAge);
      if (age < 18 || age > 100) {
        newErrors.currentAge = 'Age must be between 18 and 100';
      }
    }

    // Validate retirement year
    if (!formData.retirementYear) {
      newErrors.retirementYear = 'Retirement year is required';
    } else {
      const currentYear = new Date().getFullYear();
      const retirementYear = parseInt(formData.retirementYear);
      const currentAge = parseInt(formData.currentAge) || 0;
      
      if (retirementYear < currentYear) {
        newErrors.retirementYear = 'Retirement year cannot be in the past';
      } else if (retirementYear > currentYear + 50) {
        newErrors.retirementYear = 'Retirement year cannot be more than 50 years in the future';
      }
      
      // Check if retirement year makes sense with current age
      const yearsToRetirement = retirementYear - currentYear;
      if (currentAge + yearsToRetirement > 100) {
        newErrors.retirementYear = 'Retirement age would be over 100 years';
      }
    }

    // Validate start year
    if (!formData.startYear) {
      newErrors.startYear = 'Start year is required';
    } else {
      const currentYear = new Date().getFullYear();
      const startYear = parseInt(formData.startYear);
      if (startYear < currentYear - 1) {
        newErrors.startYear = `Start year cannot be before ${currentYear - 1}`;
      } else if (startYear > currentYear + 50) {
        newErrors.startYear = 'Start year cannot be more than 50 years in the future';
      }
    }

    // Validate projection horizon
    if (!formData.projectionHorizon) {
      newErrors.projectionHorizon = 'Projection horizon is required';
    } else {
      const horizon = parseInt(formData.projectionHorizon);
      if (horizon < 1 || horizon > 60) {
        newErrors.projectionHorizon = 'Projection horizon must be between 1 and 60 years';
      }
    }

    // Validate default rates and assumptions (decimals)
    const toNumber = (v) => (v === '' || v === null || v === undefined ? NaN : parseFloat(v));

    const expenseInflation = toNumber(formData.defaultExpenseInflation);
    if (isNaN(expenseInflation)) {
      newErrors.defaultExpenseInflation = 'Default expense inflation is required';
    } else if (expenseInflation < 0 || expenseInflation > 20) {
      newErrors.defaultExpenseInflation = 'Expense inflation must be between 0% and 20%';
    }

    const incomeGrowth = toNumber(formData.defaultIncomeGrowth);
    if (isNaN(incomeGrowth)) {
      newErrors.defaultIncomeGrowth = 'Default income growth is required';
    } else if (incomeGrowth < 0 || incomeGrowth > 50) {
      newErrors.defaultIncomeGrowth = 'Income growth must be between 0% and 50%';
    }

    const pvReturn = toNumber(formData.postRetirementPvReturn);
    if (isNaN(pvReturn)) {
      newErrors.postRetirementPvReturn = 'Post-retirement PV return is required';
    } else if (pvReturn < -50 || pvReturn > 50) {
      newErrors.postRetirementPvReturn = 'PV return must be between -50% and 50%';
    }

    const mos = toNumber(formData.marginOfSafety);
    if (isNaN(mos)) {
      newErrors.marginOfSafety = 'Margin of safety is required';
    } else if (mos <= 0 || mos > 10) {
      newErrors.marginOfSafety = 'Margin of safety must be greater than 0 and up to 10x';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    
    try {
      const setupData = {
        currentAge: parseInt(formData.currentAge),
        retirementYear: parseInt(formData.retirementYear),
        yearsToRetirement: parseInt(formData.retirementYear) - new Date().getFullYear(),
        retirementAge: parseInt(formData.currentAge) + (parseInt(formData.retirementYear) - new Date().getFullYear()),
        startYear: parseInt(formData.startYear),
        projectionHorizon: parseInt(formData.projectionHorizon),
        defaultExpenseInflation: parseFloat(formData.defaultExpenseInflation),
        defaultIncomeGrowth: parseFloat(formData.defaultIncomeGrowth),
        postRetirementPvReturn: parseFloat(formData.postRetirementPvReturn),
        marginOfSafety: parseFloat(formData.marginOfSafety),
        lastUpdated: new Date().toISOString()
      };

      const result = await updateData({ setup: setupData });
      
      if (result.success) {
        setIsSaved(true);
        setErrors({});
      } else {
        setErrors({ submit: result.error || 'Failed to save setup data' });
      }
    } catch (error) {
      setErrors({ submit: 'An error occurred while saving' });
      console.error('Error saving setup data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const calculateYearsToRetirement = () => {
    if (formData.currentAge && formData.retirementYear) {
      const currentYear = new Date().getFullYear();
      return parseInt(formData.retirementYear) - currentYear;
    }
    return null;
  };

  const calculateRetirementAge = () => {
    if (formData.currentAge && formData.retirementYear) {
      const yearsToRetirement = calculateYearsToRetirement();
      return parseInt(formData.currentAge) + yearsToRetirement;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading setup data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-foreground mb-2">Error Loading Data</h2>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button onClick={() => window.location.reload()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Sidebar />
      <Header />
      <DemoModeBanner />
      <main 
        className={`
          transition-all duration-300 ease-spring pt-16 pb-20 lg:pb-8
          ${sidebarExpanded ? 'lg:ml-64' : 'lg:ml-16'}
        `}
      >
        <div className="p-4 sm:p-6 max-w-7xl mx-auto">
            <Breadcrumb 
              items={[
                { label: 'Planning', href: '/planning' },
                { label: 'Setup', href: '/setup' }
              ]} 
            />
            
            <div className="mt-2 sm:mt-6">
              <div className="flex items-center justify-between gap-3 mb-6" data-tour="setup-header">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="Settings" size={20} className="text-primary" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-foreground">Retirement Setup</h1>
                    <p className="text-muted-foreground">Configure your basic retirement parameters</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" iconName="HelpCircle" iconPosition="left" onClick={startSetupTour} className="hidden sm:inline-flex">
                    Tour
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
                {/* Setup Form */}
                <div className="lg:col-span-2">
                  <div className="bg-card rounded-lg border border-border p-4 sm:p-6">
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div data-tour="setup-basic">
                        <div className="flex items-center gap-3 mb-6">
                          <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                            <Icon name="User" size={16} className="text-primary" />
                          </div>
                          <div>
                            <h2 className="text-lg font-semibold text-foreground">Basic Information</h2>
                            <p className="text-sm text-muted-foreground">Enter your current age and target retirement year</p>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                        <Input
                          label="Current Age"
                          name="currentAge"
                          type="number"
                          value={formData.currentAge}
                          onChange={handleInputChange}
                          placeholder="e.g., 35"
                          error={errors.currentAge}
                          required
                          min="18"
                          max="100"
                        />

                        <Input
                          label="Retirement Year"
                          name="retirementYear"
                          type="number"
                          value={formData.retirementYear}
                          onChange={handleInputChange}
                          placeholder="e.g., 2050"
                          error={errors.retirementYear}
                          required
                          min={new Date().getFullYear()}
                          max={new Date().getFullYear() + 50}
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                        <Input
                          label="Start Year"
                          name="startYear"
                          type="number"
                          value={formData.startYear}
                          onChange={handleInputChange}
                          placeholder={`e.g., ${new Date().getFullYear()}`}
                          error={errors.startYear}
                          required
                          min={new Date().getFullYear() - 1}
                          max={new Date().getFullYear() + 50}
                        />

                        <Input
                          label="Projection Horizon (years)"
                          name="projectionHorizon"
                          type="number"
                          value={formData.projectionHorizon}
                          onChange={handleInputChange}
                          placeholder="e.g., 30"
                          error={errors.projectionHorizon}
                          required
                          min="1"
                          max="60"
                        />
                        </div>
                      </div>

                      <div data-tour="setup-assumptions">
                        <div className="flex items-center gap-3 mt-8">
                          <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                            <Icon name="Sliders" size={16} className="text-primary" />
                          </div>
                          <div>
                            <h3 className="text-md font-semibold text-foreground">Global Assumptions</h3>
                            <p className="text-sm text-muted-foreground">Defaults used across projections</p>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                        <Input
                          label="Default Expense Inflation %"
                          name="defaultExpenseInflation"
                          type="number"
                          step="0.01"
                          value={formData.defaultExpenseInflation}
                          onChange={handleInputChange}
                          placeholder="e.g., 5"
                          description={!errors.defaultExpenseInflation && 'Annual inflation rate for expenses'}
                          error={errors.defaultExpenseInflation}
                          required
                          min="0"
                          max="20"
                        />

                        <Input
                          label="Default Income Growth %"
                          name="defaultIncomeGrowth"
                          type="number"
                          step="0.01"
                          value={formData.defaultIncomeGrowth}
                          onChange={handleInputChange}
                          placeholder="e.g., 8"
                          description={!errors.defaultIncomeGrowth && 'Annual growth rate for income'}
                          error={errors.defaultIncomeGrowth}
                          required
                          min="0"
                          max="50"
                        />

                        <Input
                          label="Post-Ret PV Return %"
                          name="postRetirementPvReturn"
                          type="number"
                          step="0.01"
                          value={formData.postRetirementPvReturn}
                          onChange={handleInputChange}
                          placeholder="e.g., 3.5"
                          description={!errors.postRetirementPvReturn && 'Real (post-inflation) return during retirement'}
                          error={errors.postRetirementPvReturn}
                          required
                          min="-50"
                          max="50"
                        />

                        <Input
                          label="Margin of Safety (x)"
                          name="marginOfSafety"
                          type="number"
                          step="0.01"
                          value={formData.marginOfSafety}
                          onChange={handleInputChange}
                          placeholder="e.g., 1.2"
                          description={!errors.marginOfSafety && 'Multiplier to add conservatism to projections'}
                          error={errors.marginOfSafety}
                          required
                          min="0.1"
                          max="10"
                        />
                        </div>
                      </div>

                      {errors.submit && (
                        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                          <div className="flex items-start gap-3">
                            <Icon name="AlertCircle" size={20} className="text-red-600 mt-0.5" />
                            <div>
                              <h4 className="font-medium text-red-800">Error</h4>
                              <p className="text-sm text-red-700 mt-1">{errors.submit}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="flex items-center gap-3 sm:gap-4">
                        <Button 
                          type="submit" 
                          disabled={isLoading || !effectiveCanWrite}
                          className="flex items-center gap-2"
                          title=""
                          data-tour="setup-save"
                        >
                          {isLoading ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              Saving...
                            </>
                          ) : (
                            <>
                              <Icon name="Save" size={16} />
                              {isSaved ? 'Update Setup' : 'Save Setup'}
                            </>
                          )}
                        </Button>
                        
                        {isSaved && (
                          <div className="flex items-center gap-2 text-green-600">
                            <Icon name="CheckCircle" size={16} />
                            <span className="text-sm font-medium">Setup saved successfully</span>
                          </div>
                        )}
                      </div>
                    </form>
                  </div>
                </div>

                {/* Summary Card */}
                <div className="lg:col-span-1">
                  <div className="bg-card rounded-lg border border-border p-4 sm:p-6">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-8 h-8 bg-blue-500/10 rounded-lg flex items-center justify-center">
                        <Icon name="Calculator" size={16} className="text-blue-500" />
                      </div>
                      <div>
                        <h2 className="text-lg font-semibold text-foreground">Summary</h2>
                        <p className="text-sm text-muted-foreground">Your retirement timeline</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      {formData.currentAge && formData.retirementYear ? (
                        <>
                          <div className="p-4 bg-muted/30 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-muted-foreground">Years to Retirement</span>
                              <span className="text-lg font-semibold text-foreground">
                                {calculateYearsToRetirement()}
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground">Time remaining to build your nest egg</p>
                          </div>

                          <div className="p-4 bg-muted/30 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-muted-foreground">Retirement Age</span>
                              <span className="text-lg font-semibold text-foreground">
                                {calculateRetirementAge()}
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground">Age when you plan to retire</p>
                          </div>

                          <div className="p-4 bg-primary/10 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-primary">Monthly Savings Target</span>
                              <span className="text-lg font-semibold text-primary">₹50,000</span>
                            </div>
                            <p className="text-xs text-muted-foreground">Estimated monthly savings needed</p>
                          </div>
                        </>
                      ) : (
                        <div className="text-center py-8">
                          <Icon name="Info" size={32} className="text-muted-foreground mx-auto mb-3" />
                          <p className="text-sm text-muted-foreground">
                            Enter your age and retirement year to see your retirement timeline
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Tips Card */}
                  <div className="bg-card rounded-lg border border-border p-4 sm:p-6 mt-4 sm:mt-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-8 h-8 bg-green-500/10 rounded-lg flex items-center justify-center">
                        <Icon name="Lightbulb" size={16} className="text-green-500" />
                      </div>
                      <h3 className="font-semibold text-foreground">Tips</h3>
                    </div>
                    
                    <div className="space-y-3 text-sm">
                      <div className="flex items-start gap-2">
                        <Icon name="CheckCircle" size={14} className="text-green-500 mt-0.5" />
                        <span className="text-muted-foreground">Consider inflation when setting retirement year</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Icon name="CheckCircle" size={14} className="text-green-500 mt-0.5" />
                        <span className="text-muted-foreground">Factor in healthcare costs in retirement planning</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Icon name="CheckCircle" size={14} className="text-green-500 mt-0.5" />
                        <span className="text-muted-foreground">Review and update your setup annually</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
      </main>
      <VersionSelectionModal
        isOpen={showVersionModal}
        onClose={() => setShowVersionModal(false)}
      />
    </div>
  );
};

export default Setup;
